import React, {
    FC,
    useCallback,
    useContext,
    useMemo,
    useEffect,
    useState,
} from 'react';

import { Formik } from 'formik';

import { SearchableSelectType } from 'components/inputs/searchableSelect';
import AampeSteps from 'components/shared/AampeStepper/AampeSteps';
import AampeTooltip from 'components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';
import { FormulaType } from '../../../constants/formulaType';
import ICONS from '../../../constants/icons';
import { FormulaTemplateMap } from './constants';

import { EditorContext } from 'context/EditorContext';
import { useNavContext } from 'context/NavContext';

import { getLabelFromSectionType } from './helpers';

import useQueryEmailTemplate from 'hooks/EmailTemplate/useQueryEmailTemplate';
import useCustomEmailTemplateDetail from 'hooks/custom-email-templates/useCustomEmailTemplateDetail';
import useFormulaBulkRemapTemplate from 'hooks/formulas/useFormulaBulkRemapTemplate';

import { SectionElement } from 'models/editor.model';
import {
    FormulaBaseModel,
    FormulaDetailModel,
    FormulaTypesData,
} from 'models/formula.model';
import { AampeStepperStepProps } from 'models/stepper.model';

import BasicButton from '../../buttons/basicButton';
import Step1TemplateSelection from './steps/Step1TemplateSelection';
import Step2FieldMapping from './steps/Step2Mapping';
import Step3Review from './steps/Step3Review';
import TemplateComparisonHeader from './steps/TemplateComparisonHeader';

interface FormulaTemplateModalProps {
    selectedMessages: FormulaDetailModel[];
    templateOptions: SearchableSelectType[];
    handleCancel: () => void;
    temporaryValue: SearchableSelectType | undefined;
    isAampeCustomEmailTemplate: boolean;
    setTemporaryValue: (value: SearchableSelectType) => void;
    templateId?: string;
}

const COLLAPSED_NAV_WIDTH = '90px';
const EXPANDED_NAV_WIDTH = '248px';

const FormulaTemplateModal: FC<FormulaTemplateModalProps> = ({
    selectedMessages,
    templateOptions,
    handleCancel,
    temporaryValue,
    isAampeCustomEmailTemplate,
    setTemporaryValue,
    templateId,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const { isCollapsed, setIsCollapsed } = useNavContext();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [selectedMessageForPreview, setSelectedMessageForPreview] =
        useState<FormulaBaseModel>(selectedMessages[0]);
    const [showMappingErrors, setShowMappingErrors] = useState(false);

    useEffect(() => {
        setIsCollapsed(true);
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = '';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = useCallback(() => {
        document.body.style.overflow = '';
        handleCancel();
    }, [handleCancel]);

    const bulkRemap = useFormulaBulkRemapTemplate();

    const { data: templateData } = useQueryEmailTemplate(
        temporaryValue?.value as string,
        FormulaType.email,
        !isAampeCustomEmailTemplate && !!temporaryValue,
    );
    const { data: customTemplateData } = useCustomEmailTemplateDetail(
        temporaryValue?.value as string,
        isAampeCustomEmailTemplate && !!temporaryValue,
    );

    const templateDynamicFields = useMemo<string[]>(() => {
        const fixedFields = FormulaTypesData[FormulaType.email]
            ?.getTemplate()
            .map((item: any) => item.sectionType);
        if (isAampeCustomEmailTemplate)
            return [
                ...new Set(fixedFields),
                ...new Set(
                    customTemplateData?.additional_metadata?.dynamic_keys ?? [],
                ),
            ];
        return [
            ...new Set(fixedFields),
            ...new Set(templateData?.dynamicFieldsWithModifiers ?? []),
            ...new Set(templateData?.dynamicFieldsWithoutModifiers),
        ];
    }, [customTemplateData, isAampeCustomEmailTemplate, templateData]);

    const mapOptions = useMemo<any[]>(() => {
        const _options = templateDynamicFields.map((item) => ({
            label: getLabelFromSectionType(item),
            value: item,
        }));
        return [..._options, { label: 'None', value: null }];
    }, [templateDynamicFields]);

    const keys = useMemo(() => {
        return (formula?.structure as SectionElement[])?.reduce((acc, item) => {
            return { [item.sectionType]: '', ...acc };
        }, {});
    }, [formula]);

    const initialValues = useMemo(() => ({ ...keys }), [keys]);

    const onSubmit = useCallback(
        (values: FormulaTemplateMap) => {
            if (!temporaryValue) return;

            const newFieldMapping = templateDynamicFields.reduce(
                (acc: Record<string, string | null>, dynamicKey: string) => {
                    const existingKey =
                        Object.keys(values).find(
                            (key: string) => values[key]?.value == dynamicKey,
                        ) ?? null;
                    return { ...acc, [dynamicKey]: existingKey };
                },
                {},
            );

            bulkRemap.mutate(
                {
                    messages: selectedMessages,
                    emailTemplateFieldMappings: newFieldMapping,
                    emailTemplateId: temporaryValue.value as string,
                    isAampeCustomTemplate: isAampeCustomEmailTemplate,
                    templateId,
                },
                {
                    onSuccess: () => {
                        handleClose();
                    },
                },
            );
        },
        [
            templateDynamicFields,
            temporaryValue,
            isAampeCustomEmailTemplate,
            bulkRemap,
            selectedMessages,
            templateId,
            handleClose,
        ],
    );

    const currentTemplate = useMemo(() => {
        return templateOptions.find(
            (option) =>
                option.value ===
                formula?.additional_metadata?.email_template_id,
        );
    }, [templateOptions, formula]);

    const availableTemplates = useMemo(() => {
        return templateOptions.filter(
            (option) => option.value !== currentTemplate?.value,
        );
    }, [templateOptions, currentTemplate]);

    const steps = useMemo<AampeStepperStepProps[]>(() => {
        return [
            {
                tabKey: 'select_template',
                name: 'Select template',
                title: 'Select template',
                renderComponent: (
                    <Step1TemplateSelection
                        templateOptions={templateOptions}
                        temporaryValue={temporaryValue}
                        setTemporaryValue={setTemporaryValue}
                        currentTemplateId={
                            formula?.additional_metadata?.email_template_id ??
                            ''
                        }
                    />
                ),
                nextButtonProps: { isDisabled: !temporaryValue },
            },
            {
                tabKey: 'map_elements',
                name: 'Map elements',
                title: 'Map elements',
                renderComponent: (
                    <Step2FieldMapping
                        selectedMessages={selectedMessages}
                        selectedMessageForPreview={selectedMessageForPreview}
                        setSelectedMessageForPreview={
                            setSelectedMessageForPreview
                        }
                        mapOptions={mapOptions}
                        showMappingErrors={showMappingErrors}
                    />
                ),
                nextButtonProps: { isDisabled: !temporaryValue },
                disableStepInfo: {
                    isDisabled: !temporaryValue,
                    tooltipText: 'Select a template first',
                },
            },
            {
                tabKey: 'review',
                name: 'Review',
                title: 'Review & confirm',
                renderComponent: (
                    <Step3Review selectedMessages={selectedMessages} />
                ),
                nextButtonProps: { isDisabled: !temporaryValue },
                disableStepInfo: {
                    isDisabled: !temporaryValue,
                    tooltipText: 'Select a template first',
                },
            },
        ];
    }, [
        templateOptions,
        temporaryValue,
        setTemporaryValue,
        formula,
        mapOptions,
        selectedMessages,
        selectedMessageForPreview,
        showMappingErrors,
    ]);

    const currentStep = steps[currentStepIndex];
    const isLastStep = currentStepIndex === steps.length - 1;
    const isFirstStep = currentStepIndex === 0;
    const isNextDisabled = currentStep?.nextButtonProps?.isDisabled || false;

    const handleBack = useCallback(() => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex((prev) => prev - 1);
        }
    }, [currentStepIndex]);

    const handleStepClick = useCallback(
        (index: number) => {
            const step = steps[index];
            if (step && !step.disableStepInfo?.isDisabled) {
                setCurrentStepIndex(index);
            }
        },
        [steps],
    );

    useEffect(() => {
        setShowMappingErrors(false);
    }, [currentStepIndex]);

    const isAllFieldsMapped = useCallback(
        (values: Record<string, any>) => {
            if (!keys) return false;
            return Object.keys(keys).every(
                (key) => values[key] !== '' && values[key] !== undefined,
            );
        },
        [keys],
    );
    const handleNext = useCallback(
        (values?: Record<string, any>) => {
            if (isNextDisabled) return;
            if (
                currentStepIndex === 1 &&
                values &&
                !isAllFieldsMapped(values)
            ) {
                setShowMappingErrors(true);
                return;
            }
            if (currentStepIndex < steps.length - 1) {
                setCurrentStepIndex((prev) => prev + 1);
            }
        },
        [currentStepIndex, steps.length, isAllFieldsMapped, isNextDisabled],
    );

    const isOnSelectTemplateStep = currentStep?.tabKey === 'select_template';

    return (
        <Formik
            initialValues={initialValues ?? {}}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {({ handleSubmit, values }) => (
                <FullScreenOverlay navCollapsed={isCollapsed}>
                    <FullScreenContainer>
                        {/* Header */}
                        <Header>
                            <HeaderTitle>
                                Map selected messages to new template
                            </HeaderTitle>
                            <StepperWrapper>
                                <AampeSteps
                                    dividerType="horizontal"
                                    steps={steps}
                                    currentStepIndex={currentStepIndex}
                                    onClick={handleStepClick}
                                    showStepsAsCompleted={false}
                                />
                            </StepperWrapper>
                            <CloseButton onClick={handleClose}>
                                <ICONS.close size={20} />
                            </CloseButton>
                        </Header>

                        {/* Template comparison bar */}
                        <BelowHeaderSection>
                            <TemplateComparisonHeader
                                currentTemplate={currentTemplate}
                                newTemplate={temporaryValue}
                                templateOptions={availableTemplates}
                                onTemplateChange={setTemporaryValue}
                                showDropdown={isOnSelectTemplateStep}
                            />
                        </BelowHeaderSection>

                        {/* Step content */}
                        <ContentArea>
                            <StepContent>
                                {currentStep?.renderComponent}
                            </StepContent>

                            {/* Navigation */}
                            <ActionButtons>
                                {isFirstStep ? (
                                    <BasicButton
                                        name="Cancel"
                                        isPrimary={false}
                                        isOutlined
                                        onClick={handleClose}
                                    />
                                ) : (
                                    <BasicButton
                                        name="Back"
                                        isPrimary={false}
                                        isOutlined
                                        onClick={handleBack}
                                    />
                                )}

                                {isLastStep ? (
                                    <AampeTooltip
                                        title={
                                            !isAllFieldsMapped(values)
                                                ? 'Please map all fields before continuing'
                                                : ''
                                        }
                                        placement="top"
                                    >
                                        <span>
                                            <BasicButton
                                                name="Map and change template"
                                                onClick={() => handleSubmit()}
                                                disabled={
                                                    !isAllFieldsMapped(values)
                                                }
                                            />
                                        </span>
                                    </AampeTooltip>
                                ) : (
                                    <AampeTooltip
                                        title={
                                            isNextDisabled
                                                ? 'Select a template first'
                                                : currentStepIndex === 1 &&
                                                  !isAllFieldsMapped(values)
                                                ? 'Please map all fields before continuing'
                                                : ''
                                        }
                                        placement="top"
                                    >
                                        <BasicButton
                                            name="Continue"
                                            onClick={() => handleNext(values)}
                                        />
                                    </AampeTooltip>
                                )}
                            </ActionButtons>
                        </ContentArea>
                    </FullScreenContainer>
                </FullScreenOverlay>
            )}
        </Formik>
    );
};

const FullScreenOverlay = styled.div<{ navCollapsed: boolean }>(
    ({ navCollapsed }) => ({
        position: 'fixed',
        top: 0,
        left: navCollapsed ? COLLAPSED_NAV_WIDTH : EXPANDED_NAV_WIDTH,
        right: 0,
        bottom: 0,
        backgroundColor: aampeTheme.colors.white,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        transition: 'left 300ms ease-in-out',
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.08)',
    }),
);

const FullScreenContainer = styled.div({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
});

const Header = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: aampeTheme.spacing4(4, 6),
    borderBottom: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    backgroundColor: aampeTheme.colors.white,
    gap: aampeTheme.spacing4(6),
    flexShrink: 0,
});

const HeaderTitle = styled.h2({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.neutral09,
    margin: 0,
    flexShrink: 0,
});

const StepperWrapper = styled.div({
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '600px',
});

const CloseButton = styled.button({
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: aampeTheme.spacing4(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: aampeTheme.colors.textAccessibleWG01,
    flexShrink: 0,
    '&:hover': {
        color: aampeTheme.colors.neutral09,
    },
});

const BelowHeaderSection = styled.div({
    flexShrink: 0,
    backgroundColor: aampeTheme.colors.backgroundWG01,
    padding: aampeTheme.spacing4(4, 6, 0, 6),
});

const ContentArea = styled.div({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: aampeTheme.colors.backgroundWG01,
});

const StepContent = styled.div({
    flex: 1,
    overflow: 'auto',
    padding: aampeTheme.spacing4(6),
});

const ActionButtons = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    padding: aampeTheme.spacing4(4, 6),
    backgroundColor: aampeTheme.colors.white,
    borderTop: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    flexShrink: 0,
});

export default FormulaTemplateModal;
